import type { League, LeagueInfo } from '@library/types/leagues';
import {
  getBulkLeagueIdGroups,
  getLeagueInfo,
  getLeagueOptions,
  getLeaguesList
} from '@bo-utils/leagues';
import { useEffect, useMemo, useState } from 'react';
import { useGetAllLeaguesQuery } from '@bo-services/leaguesApi';

export function useLeagues({
  initialId
}: {
  initialId?: string;
} = {}) {
  const {
    isLoading,
    isFetching,
    data: leagueData,
    isError: isLeagueError
  } = useGetAllLeaguesQuery();

  const isLeagueLoading = isLoading || isFetching;

  const storedId = initialId ?? localStorage.getItem('leagueId') ?? undefined;

  const [selectedLeagueId, setSelectedLeagueId] = useState<
    League['id'] | undefined
  >(storedId);

  const leaguesList = useMemo(() => getLeaguesList(leagueData), [leagueData]);

  const leagueOptions = useMemo(
    () => getLeagueOptions(leagueData),
    [leagueData]
  );

  const handleSelectLeague = (leagueId: League['id']) => {
    setSelectedLeagueId(leagueId);
  };

  useEffect(() => {
    if (!selectedLeagueId && leaguesList?.length > 0) {
      handleSelectLeague(leaguesList[0]?.id);
    }
  }, [leaguesList, selectedLeagueId]);

  useEffect(() => {
    if (selectedLeagueId && (!storedId || storedId !== selectedLeagueId)) {
      localStorage.setItem('leagueId', selectedLeagueId ?? '');
    }
  }, [selectedLeagueId, storedId]);

  const leagueInfo = useMemo(
    (): LeagueInfo | undefined =>
      getLeagueInfo({ leagueData, selectedLeagueId }),
    [leagueData, selectedLeagueId]
  );

  const bulkLeagueIdGroups = useMemo(
    () => getBulkLeagueIdGroups({ leagueData }),
    [leagueData]
  );

  return {
    bulkLeagueIdGroups,
    handleSelectLeague,
    isLeagueError,
    isLeagueLoading,
    selectedLeagueId,
    leagueInfo,
    leaguesList,
    leagueOptions
  };
}
